import { useCallback, useMemo, useState } from 'react'

import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { useAuthenticatedAccount } from 'src/features/auth/context'
import { GetPatientReturn } from 'src/features/patient/api'
import { useMirohaToast } from 'src/lib/chakra-theme/components/toast/use-miroha-toast'
import { WorksheetStatus as GqlWorksheetStatus } from 'src/lib/gql-client'
import { ObservationType } from 'src/lib/gql-client/client-preset/graphql'

import {
  getPreviousEditableFieldValues,
  PreviousEditableFieldValues,
} from '../../api'
import { useWorksheetValueContext } from '../../context/WorksheetValue'
import { Worksheet, WorksheetAction, WorksheetDetail } from '../../types'
import { toDateString, toWorksheetDate } from '../../utils/date'
import { filterOutRepeatableSections } from '../../utils/fieldValueFilters'
import {
  canPerformWorksheetAction,
  saveActionStatusMap,
} from '../../utils/permissions'
type Props = {
  worksheet: WorksheetDetail
  observationType: ObservationType
  patient: GetPatientReturn
}

// 保存済みとみなすステータス（GraphQLの型）
const SAVED_STATUSES: GqlWorksheetStatus[] = [
  'Saved',
  'Confirmed',
  'DEPresend',
  'DEAwaitingReceipt',
  'DEReceived',
  'DEReject',
]

export const CopyPreviousValues: React.FC<Props> = ({
  worksheet,
  observationType,
  patient,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const toast = useMirohaToast()
  const {
    onChangeTextFieldValue,
    onChangeTextareaFieldValue,
    onChangeNumberFieldValue,
    onChangeDateFieldValue,
    onChangeNullableDateFieldValue,
    onChangeTimeFieldValue,
    onChangeCheckboxFieldValue,
    onChangeRadioFieldValue,
    onChangeSelectMenuFieldValue,
  } = useWorksheetValueContext()

  const {
    account: { selectedTrialHospitalRole, member: currentMember },
  } = useAuthenticatedAccount()

  const [isLoading, setIsLoading] = useState(false)

  // 同じpatientObservation内のワークシート一覧を取得
  const observation = useMemo(() => {
    return [
      ...patient.observationVisits,
      ...patient.observationFollowUps,
      ...patient.observationDocumentExchanges,
    ].find(o => o.patientObservationUid === worksheet.patientObservationUid)
  }, [patient, worksheet.patientObservationUid])

  // コピー元となる前回のワークシートを検索
  const previousWorksheetUid = useMemo(() => {
    if (!observation) return null

    // 同じテンプレート、保存済み、自身より前のindexのワークシートをフィルタ
    const candidates = observation.worksheets
      .filter(ws => {
        return (
          ws.worksheetSchemaUid === worksheet.worksheetSchemaUid &&
          ws.index < worksheet.index &&
          SAVED_STATUSES.includes(ws.latestWorksheetLog.status)
        )
      })
      .sort((a, b) => b.index - a.index) // indexの降順でソート（最も新しいものが先頭）

    return candidates.length > 0 ? candidates[0].worksheetUid : null
  }, [observation, worksheet])

  // 値をコピーする処理
  const copyFieldValues = useCallback(
    (data: PreviousEditableFieldValues) => {
      const schema = worksheet.schema

      // RepeatableSectionとその子フィールドを除外
      const filteredFieldValues = filterOutRepeatableSections(
        data.fieldValues,
        schema,
      )

      // フィールド値を反映
      filteredFieldValues.forEach(fieldValue => {
        const { fid, index, type } = fieldValue

        switch (type) {
          case 'text':
            onChangeTextFieldValue({
              fid,
              index,
              textValue: fieldValue.textValue,
            })
            break
          case 'textarea':
            onChangeTextareaFieldValue({
              fid,
              index,
              textareaValue: fieldValue.textareaValue,
            })
            break
          case 'number':
            onChangeNumberFieldValue({
              fid,
              index,
              numberValue: fieldValue.numberValue,
            })
            break
          case 'date':
            onChangeDateFieldValue({
              fid,
              index,
              dateValue: fieldValue.dateValue,
            })
            break
          case 'nullableDate':
            onChangeNullableDateFieldValue({
              fid,
              index,
              nullableDateValue: toDateString(
                toWorksheetDate(fieldValue.nullableDateValue),
              ),
            })
            break
          case 'time':
            onChangeTimeFieldValue({
              fid,
              index,
              timeValue: fieldValue.timeValue,
            })
            break
          case 'checkbox':
            onChangeCheckboxFieldValue({
              fid,
              index,
              checkboxValue: fieldValue.checkboxValue,
            })
            break
          case 'radio':
            onChangeRadioFieldValue({
              fid,
              index,
              radioValue: fieldValue.radioValue,
            })
            break
          case 'selectMenu':
            onChangeSelectMenuFieldValue({
              fid,
              index,
              selectMenuValue: fieldValue.selectMenuValue,
            })
            break
          case 'repeatableSection':
          case 'file':
          case 'cleared':
            // 繰り返しセクション,ファイル,クリア済みはコピーしない
            break
          default:
            // 未知のtypeの場合は警告を出す
            console.warn('Unknown field value type:', fieldValue)
        }
      })

      toast({
        title: '前回値を反映しました',
        status: 'success',
      })
      onClose()
    },
    [
      worksheet,
      onChangeTextFieldValue,
      onChangeTextareaFieldValue,
      onChangeNumberFieldValue,
      onChangeDateFieldValue,
      onChangeNullableDateFieldValue,
      onChangeTimeFieldValue,
      onChangeCheckboxFieldValue,
      onChangeRadioFieldValue,
      onChangeSelectMenuFieldValue,
      toast,
      onClose,
    ],
  )

  const handleClick = useCallback(() => {
    onOpen()
  }, [onOpen])

  const handleConfirm = useCallback(async () => {
    if (!previousWorksheetUid) return

    setIsLoading(true)
    try {
      // ボタン押下時に前回のワークシートデータを取得
      const data = await getPreviousEditableFieldValues({
        worksheetUid: previousWorksheetUid as Worksheet['uid'],
      })

      // データ取得後、値をコピー
      copyFieldValues(data)
    } catch (_error) {
      toast({
        title: 'フィールド値の読み込みに失敗しました',
        status: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }, [previousWorksheetUid, copyFieldValues, toast])

  const saveAction: WorksheetAction =
    selectedTrialHospitalRole === 'Main' ? 'deMainSave' : 'dePartnerSave'

  if (
    observationType !== 'DocumentExchange' ||
    !canPerformWorksheetAction(
      saveAction,
      currentMember.role,
      selectedTrialHospitalRole,
    )
  ) {
    return null
  }
  if (!saveActionStatusMap[saveAction]?.includes(worksheet.status)) {
    return null
  }
  // コピー元となるワークシートが存在しない場合はボタンを表示しない
  if (!previousWorksheetUid) {
    return null
  }

  return (
    <>
      <Button colorScheme="blue" variant="outline" onClick={handleClick}>
        前回値の反映
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>前回値の反映</ModalHeader>
          <ModalBody>
            <Text>
              前回値から入力値を反映しますか？
              <br />
              ※すでにフォームに入力がある場合、内容が破棄されるのでご注意ください。
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" onClick={onClose}>
              いいえ
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleConfirm}
              isLoading={isLoading}
              ml={3}
            >
              はい
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
